import React, { useEffect, useState } from 'react';
import { Modal, TextField, MenuItem, FormControl, Checkbox, Typography, Box, Button } from '@mui/material';
import ConfirmModal from '../common/modal/ConfirmModal';
import { FormInputText } from '../../form-components/FormInputText';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '../CalendarIcon';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { passChangeStopCount, passChangeStopPeriod } from '../../api/ticket';
import { FormInputDate } from '../../form-components/FormInputDate';
import { useToast } from 'use-toast-mui';
import { errorMessages, validateDateRange } from '../../utils/methods';
import { stopPassById, stopCancelPassById, modifyStopPassById } from '../../api/member-management';
import { getAllStaffs } from '../../api/staff';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  FormRow,
  FormLabel,
  DatePickerWrapper,
  DatePickerSeparator,
  SmallInputWrapper,
  SmallTextField,
  ChangeButton,
  InfoDisplayWrapper,
  InfoDateText,
  InfoDaysWrapper,
  InfoDaysNumber,
  InfoDaysText,
  ButtonWrapper,
  CancelButton,
  ReleaseButton,
  SaveButton,
  StyledSelect,
} from './ChangeTFreezyModalStyled';

export default function ChangePFreezyModal({ modal, setModal, bigUser, ticket, user, getAllTickets, staffList = [] }) {
  const { t } = useTranslation();
  const toast = useToast();
  const userApp = useSelector(state => state.user.user);
  const branchId = userApp?.branch?.branchId;
  const headBranchId = userApp?.headBranch?.headBranchId;

  const { data: staffData } = useQuery({
    queryKey: ['staffList', headBranchId, branchId],
    queryFn: () => getAllStaffs(bigUser.token, `headBranchId=${headBranchId}&branchId=${branchId}&page=1&limit=100`),
    enabled: !!bigUser?.token && !!branchId,
  });
  const staffListData = staffData?.content ?? [];

  // stopCount/stopPeriod null = 무제한 (0 = 중지불가, 양수 = N회/N일 제한)
  const [count, setCount] = useState('');
  const [period, setPeriod] = useState('');
  const [countUnlimited, setCountUnlimited] = useState(false);
  const [periodUnlimited, setPeriodUnlimited] = useState(false);
  const [appliedCount, setAppliedCount] = useState(null);
  const [appliedPeriod, setAppliedPeriod] = useState(null);

  // 확인창 상태
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', fromValue: '', toValue: '', onConfirm: null });

  // 모달 열릴 때 ticket에서 초기값 로드
  useEffect(() => {
    if (modal?.open4 && ticket) {
      setCount(ticket.stopCount ?? 0);
      setPeriod(ticket.stopPeriod ?? 0);
      setCountUnlimited(ticket.stopCount === null);
      setPeriodUnlimited(ticket.stopPeriod === null);
      setAppliedCount(ticket.stopCount);
      setAppliedPeriod(ticket.stopPeriod);
    }
  }, [modal?.open4, ticket]);

  const { handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      ...ticket,
      stopTimes: ticket?.stoppedDays ?? '',
      startDate: ticket?.stoppedDate ?? null,
      endDate: ticket?.stoppedEndDate ?? null,
      managerId: ticket?.stopManagerId ?? '',
      memo: ticket?.stopMemo ?? '',
    },
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedStopTimes = watch('stopTimes');
  const watchedManagerId = watch('managerId');

  const isStopped = ticket?.status === 'STOPPED';
  // 체크박스가 아니라 적용된(서버 반영) 값 기준. 횟수/일수 각각 null(무제한)이거나 1 이상이어야 중지 가능.
  const hasStopAllowance = value => value === null || Number(value) >= 1;
  const isActive =
    isStopped ||
    (ticket?.status === 'ACTIVE' &&
      hasStopAllowance(appliedCount) && hasStopAllowance(appliedPeriod));
  // 중지예정(시작일 미래)은 ACTIVE 로 저장되지만 만료일·중지횟수가 선반영돼 있어 해제할 수 있다.
  const isStopScheduled =
    ticket?.status === 'ACTIVE' &&
    !!ticket?.stoppedDate &&
    moment(ticket.stoppedDate).startOf('day').isAfter(moment().startOf('day'));
  const canRelease = ticket?.status !== 'ACTIVE' || isStopScheduled;

  const endDatePickerMinDate = watchedStartDate || null;

  useEffect(() => {
    const startDate = watch('startDate');
    const endDate = watch('endDate');

    if (startDate && endDate) {
      const dayDifference = moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days') + 1;
      setValue('stopTimes', dayDifference);
    }
  }, [watch('startDate'), watch('endDate'), setValue]);

  const onClose = () => {
    reset();
    setModal({ ...modal, open4: false });
  };

  // ============================================
  // 해제 확인 모달
  // ============================================
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState({ open: false, data: null, onConfirm: null });

  const cancelPassByMemberId = () => {
    const stoppedDate = ticket?.stoppedDate ? moment(ticket.stoppedDate) : null;
    const stoppedEndDate = ticket?.stoppedEndDate ? moment(ticket.stoppedEndDate) : null;
    const totalStoppedDays = ticket?.stoppedDays ?? 0;
    const currentExpireDate = ticket?.expireDate ? moment(ticket.expireDate).format('YYYY-MM-DD') : '-';
    const today = moment().startOf('day');
    const todayStr = today.format('YYYY-MM-DD');

    let usedDays = 0;
    let restoreDays = 0;
    let status = '';

    if (stoppedDate && !stoppedDate.clone().startOf('day').isBefore(today)) {
      usedDays = 0;
      restoreDays = totalStoppedDays;
      status = '중지 시작 전 해제 (전체 복원)';
    } else if (stoppedDate && stoppedEndDate) {
      usedDays = today.diff(stoppedDate.clone().startOf('day'), 'days');
      const remaining = stoppedEndDate.clone().startOf('day').diff(today, 'days') + 1;
      restoreDays = Math.max(remaining, 0);
      if (restoreDays > 0) {
        status = '중지 중 해제 (남은 기간 복원)';
      } else {
        usedDays = totalStoppedDays;
        status = '중지 종료일 경과 (복원 없음)';
      }
    }

    const newExpireDate = ticket?.expireDate
      ? moment(ticket.expireDate).subtract(restoreDays, 'days').format('YYYY-MM-DD')
      : '-';

    setCancelConfirmDialog({
      open: true,
      data: {
        today: todayStr,
        stoppedDate: stoppedDate ? stoppedDate.format('YYYY-MM-DD') : '-',
        stoppedEndDate: stoppedEndDate ? stoppedEndDate.format('YYYY-MM-DD') : '-',
        totalStoppedDays,
        usedDays,
        restoreDays,
        oldExpire: currentExpireDate,
        newExpire: newExpireDate,
        status,
      },
      onConfirm: async () => {
        setCancelConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          await stopCancelPassById(bigUser.token, user?.memberId, ticket?.memberCoursePassId);
          toast.success(t('Successfully saved'));
          getAllTickets();
          onClose();
        } catch (error) {
          toast.error(errorMessages(error));
        }
      },
    });
  };

  // ============================================
  // 저장 확인 모달
  // ============================================
  const [stopConfirmDialog, setStopConfirmDialog] = useState({ open: false, data: null, onConfirm: null });

  const stopPassByMemberId = async values => {
    const { stopTimes, startDate, endDate, managerId, memo } = values;

    if (!startDate) {
      toast.error('중지 시작 날짜를 선택해주세요');
      return;
    }

    if (!endDate) {
      toast.error('중지 종료 날짜를 선택해주세요');
      return;
    }

    if (!validateDateRange(startDate, endDate)) {
      toast.error('기간을 다시 확인해주세요');
      return;
    }

    const stopDays = Number(stopTimes);
    const currentExpireDate = ticket?.expireDate ? moment(ticket.expireDate).format('YYYY-MM-DD') : '-';
    const formattedStart = moment(startDate).format('YYYY-MM-DD');
    const formattedEnd = moment(endDate).format('YYYY-MM-DD');
    const isPastStop = moment(endDate).isBefore(moment().startOf('day'));

    let data;
    if (isStopped) {
      const oldStoppedDays = ticket?.stoppedDays ?? 0;
      const diff = stopDays - oldStoppedDays;
      const modifiedExpire = ticket?.expireDate ? moment(ticket.expireDate).add(diff, 'days').format('YYYY-MM-DD') : '-';
      data = {
        type: 'modify',
        startDate: formattedStart,
        endDate: formattedEnd,
        stopDays,
        oldStoppedDays,
        diff,
        oldExpire: currentExpireDate,
        newExpire: modifiedExpire,
        isPastStop,
      };
    } else {
      const newExpireDate = ticket?.expireDate ? moment(ticket.expireDate).add(stopDays, 'days').format('YYYY-MM-DD') : '-';
      data = {
        type: 'new',
        startDate: formattedStart,
        endDate: formattedEnd,
        stopDays,
        oldExpire: currentExpireDate,
        newExpire: newExpireDate,
        isPastStop,
      };
    }

    setStopConfirmDialog({
      open: true,
      data,
      onConfirm: async () => {
        setStopConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          const payload = {
            stopPeriod: stopDays,
            startDate: formattedStart,
            endDate: formattedEnd,
            managerId: managerId || null,
            memo: memo || null,
          };
          if (isStopped) {
            await modifyStopPassById(bigUser.token, user?.memberId, ticket?.memberCoursePassId, payload);
          } else {
            await stopPassById(bigUser.token, user?.memberId, ticket?.memberCoursePassId, payload);
          }
          toast.success(t('Successfully saved'));
          getAllTickets();
          onClose();
        } catch (error) {
          toast.error(errorMessages(error));
        }
      },
    });
  };

  const requestStopCountChange = () => {
    const newValue = countUnlimited ? null : count;
    const fromLabel = appliedCount === null ? '무제한' : `${appliedCount}회`;
    const toLabel = newValue === null ? '무제한' : `${newValue}회`;
    setConfirmDialog({
      open: true,
      type: '중지 가능 횟수',
      fromValue: fromLabel,
      toValue: toLabel,
      onConfirm: async () => {
        try {
          await passChangeStopCount(ticket?.memberCoursePassId, newValue, bigUser.token);
          toast.success(t('성공적으로 적용되었습니다.'));
          setAppliedCount(newValue);
          getAllTickets();
        } catch (error) {
          toast.error(errorMessages(error));
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  const requestStopPeriodChange = () => {
    const newValue = periodUnlimited ? null : period;
    const fromLabel = appliedPeriod === null ? '무제한' : `${appliedPeriod}일`;
    const toLabel = newValue === null ? '무제한' : `${newValue}일`;
    setConfirmDialog({
      open: true,
      type: '중지 가능 일수',
      fromValue: fromLabel,
      toValue: toLabel,
      onConfirm: async () => {
        try {
          await passChangeStopPeriod(ticket?.memberCoursePassId, newValue, bigUser.token);
          toast.success(t('성공적으로 적용되었습니다.'));
          setAppliedPeriod(newValue);
          getAllTickets();
        } catch (error) {
          toast.error(errorMessages(error));
        }
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  return (
    <>
    <Modal
      open={modal?.open4}
      onClose={onClose}
      aria-labelledby="freeze-modal-title"
      aria-describedby="freeze-modal-description">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>중지설정</ModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <FormSection>
            {/* 중지 기간 */}
            <FormRow>
              <FormLabel>중지 기간</FormLabel>
              <DatePickerWrapper>
                <FormInputDate
                  name="startDate"
                  control={control}
                  placeholder="YYYY-MM-DD"
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={params => (
                    <TextField
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          borderRadius: '8px',
                        },
                        '& .MuiSvgIcon-root': {
                          fill: '#0F7F85 !important',
                        },
                      }}
                      size="small"
                      {...params}
                    />
                  )}
                />
                <DatePickerSeparator>~</DatePickerSeparator>
                <FormInputDate
                  name="endDate"
                  control={control}
                  placeholder="YYYY-MM-DD"
                  minDate={endDatePickerMinDate}
                  disabled={!watchedStartDate}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={params => (
                    <TextField
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          borderRadius: '8px',
                        },
                        '& .MuiSvgIcon-root': {
                          fill: '#0F7F85 !important',
                        },
                      }}
                      size="small"
                      {...params}
                    />
                  )}
                />
              </DatePickerWrapper>
            </FormRow>

            {/* 잔여 중지 가능 횟수 */}
            <FormRow>
              <FormLabel>잔여 중지 가능 횟수</FormLabel>
              <SmallInputWrapper sx={{ width: 'auto' }}>
                {countUnlimited ? (
                  <Typography sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600, fontSize: '14px' }}>무제한</Typography>
                ) : (
                  <>
                    <SmallTextField type="number" size="small" value={count} onChange={e => setCount(e.target.value)} />
                    <Typography sx={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>회</Typography>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
                  onClick={() => { setCountUnlimited(prev => { if (!prev) setCount(0); return !prev; }); }}>
                  <Checkbox size="small" checked={countUnlimited} sx={{ padding: '4px' }} />
                  <Typography sx={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>무제한</Typography>
                </Box>
                <ChangeButton onClick={requestStopCountChange}>변경</ChangeButton>
              </SmallInputWrapper>
            </FormRow>

            {/* 잔여 중지 가능 일수 */}
            <FormRow>
              <FormLabel>잔여 중지 가능 일수</FormLabel>
              <SmallInputWrapper sx={{ width: 'auto' }}>
                {periodUnlimited ? (
                  <Typography sx={{ width: '80px', textAlign: 'center', color: '#0F7F85', fontWeight: 600, fontSize: '14px' }}>무제한</Typography>
                ) : (
                  <>
                    <SmallTextField type="number" size="small" value={period} onChange={e => setPeriod(e.target.value)} />
                    <Typography sx={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>일</Typography>
                  </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}
                  onClick={() => { setPeriodUnlimited(prev => { if (!prev) setPeriod(0); return !prev; }); }}>
                  <Checkbox size="small" checked={periodUnlimited} sx={{ padding: '4px' }} />
                  <Typography sx={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>무제한</Typography>
                </Box>
                <ChangeButton onClick={requestStopPeriodChange}>변경</ChangeButton>
              </SmallInputWrapper>
            </FormRow>

            {/* 관리 담당자 */}
            <FormRow>
              <FormLabel>관리 담당자</FormLabel>
              <FormControl sx={{ width: '163px' }} size="small">
                <StyledSelect
                  value={watchedManagerId}
                  onChange={e => setValue('managerId', e.target.value)}
                  displayEmpty>
                  <MenuItem value="">
                    <em>선택</em>
                  </MenuItem>
                  {staffListData.map(staff => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </FormRow>

            {/* 메모 */}
            <FormRow>
              <FormLabel>메모</FormLabel>
              <FormInputText
                name="memo"
                control={control}
                placeholder="메모를 입력해 주세요"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '44px',
                    borderRadius: '8px',
                  },
                }}
              />
            </FormRow>

            {/* 중지일 안내 */}
            <FormRow>
              <FormLabel>중지일 안내</FormLabel>
              <InfoDisplayWrapper>
                <InfoDaysWrapper>
                  <InfoDaysNumber>{watchedStopTimes || 0}</InfoDaysNumber>
                  <InfoDaysText>일간</InfoDaysText>
                </InfoDaysWrapper>
              </InfoDisplayWrapper>
            </FormRow>
          </FormSection>

          <ButtonWrapper>
            <CancelButton onClick={onClose}>취소</CancelButton>
            <ReleaseButton isActive={canRelease} disabled={!canRelease} onClick={cancelPassByMemberId}>
              해제
            </ReleaseButton>
            <SaveButton isActive={isActive} disabled={!isActive} onClick={handleSubmit(stopPassByMemberId)}>
              저장
            </SaveButton>
          </ButtonWrapper>
        </ModalContent>
      </ModalContainer>
    </Modal>

    {/* 횟수/일수 변경 확인 모달 */}
    <ConfirmModal
      open={confirmDialog.open}
      onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      onConfirm={confirmDialog.onConfirm}
      message={`${confirmDialog.type}\n${confirmDialog.fromValue} → ${confirmDialog.toValue}(으)로 변경\n즉시 적용됩니다.`}
      highlightText="변경"
      highlightColor="#0F7F85"
      cancelText="취소"
      confirmText="변경"
      confirmColor="#0F7F85"
    />

    {/* 중지 저장 확인 모달 */}
    <Modal open={stopConfirmDialog.open} onClose={() => setStopConfirmDialog(prev => ({ ...prev, open: false }))}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 440, bgcolor: '#fff', borderRadius: '16px', boxShadow: 24, p: '28px 32px',
      }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#222', mb: 2.5, textAlign: 'center' }}>
          {stopConfirmDialog.data?.type === 'modify' ? '중지 기간 변경 확인' : '중지 설정 확인'}
        </Typography>

        {stopConfirmDialog.data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#F5F5F5', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: 13, color: '#888', minWidth: 70 }}>중지 기간</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                {stopConfirmDialog.data.startDate} ~ {stopConfirmDialog.data.endDate}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F7F85', ml: 'auto' }}>
                {stopConfirmDialog.data.stopDays}일
              </Typography>
            </Box>

            {stopConfirmDialog.data.type === 'modify' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#F5F5F5', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: 13, color: '#888', minWidth: 70 }}>중지 일수</Typography>
                <Typography sx={{ fontSize: 14, color: '#999', textDecoration: 'line-through' }}>
                  {stopConfirmDialog.data.oldStoppedDays}일
                </Typography>
                <Typography sx={{ fontSize: 14, color: '#333' }}>→</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                  {stopConfirmDialog.data.stopDays}일
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, ml: 'auto', color: '#0F7F85' }}>
                  {stopConfirmDialog.data.diff > 0 ? '+' : ''}{stopConfirmDialog.data.diff}일
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#E8F5E9', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: 13, color: '#888', minWidth: 70 }}>만료일</Typography>
              <Typography sx={{ fontSize: 14, color: '#999', textDecoration: 'line-through' }}>
                {stopConfirmDialog.data.oldExpire}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#333' }}>→</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#2E7D32' }}>
                {stopConfirmDialog.data.newExpire}
              </Typography>
              {stopConfirmDialog.data.type === 'new' && (
                <Typography sx={{ fontSize: 12, color: '#0F7F85', ml: 'auto' }}>
                  +{stopConfirmDialog.data.stopDays}일 연장
                </Typography>
              )}
            </Box>

            {stopConfirmDialog.data.isPastStop && (
              <Box sx={{ p: '10px 14px', bgcolor: '#FFF3E0', borderRadius: '8px', border: '1px solid #FFB74D' }}>
                <Typography sx={{ fontSize: 13, color: '#E65100', fontWeight: 500 }}>
                  ※ 중지 종료일이 이미 지났으므로 기간 연장만 반영되고 중지 상태로 전환되지 않습니다.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button fullWidth onClick={() => setStopConfirmDialog(prev => ({ ...prev, open: false }))}
            sx={{ height: 46, borderRadius: '8px', border: '1px solid #E0E0E0', color: '#333', fontSize: 15, fontWeight: 500 }}>
            취소
          </Button>
          <Button fullWidth onClick={stopConfirmDialog.onConfirm}
            sx={{ height: 46, borderRadius: '8px', bgcolor: '#0F7F85', color: '#fff', fontSize: 15, fontWeight: 600, '&:hover': { bgcolor: '#0d6e73' } }}>
            저장
          </Button>
        </Box>
      </Box>
    </Modal>

    {/* 중지 해제 확인 모달 */}
    <Modal open={cancelConfirmDialog.open} onClose={() => setCancelConfirmDialog(prev => ({ ...prev, open: false }))}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 440, bgcolor: '#fff', borderRadius: '16px', boxShadow: 24, p: '28px 32px',
      }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#222', mb: 2.5, textAlign: 'center' }}>
          중지 해제 확인
        </Typography>

        {cancelConfirmDialog.data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#F5F5F5', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: 13, color: '#888', minWidth: 80 }}>오늘 날짜</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                {cancelConfirmDialog.data.today}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#F5F5F5', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: 13, color: '#888', minWidth: 80 }}>중지 기간</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                {cancelConfirmDialog.data.stoppedDate} ~ {cancelConfirmDialog.data.stoppedEndDate}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F7F85', ml: 'auto' }}>
                총 {cancelConfirmDialog.data.totalStoppedDays}일
              </Typography>
            </Box>

            <Box sx={{ p: '12px 14px', bgcolor: '#F5F5F5', borderRadius: '8px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, color: '#666' }}>사용한 중지일수</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F7F85' }}>
                  {cancelConfirmDialog.data.usedDays}일
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 13, color: '#666' }}>남은 중지일수 (만료일에서 차감)</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0F7F85' }}>
                  {cancelConfirmDialog.data.restoreDays}일
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', bgcolor: '#E8F5E9', borderRadius: '8px' }}>
              <Typography sx={{ fontSize: 13, color: '#888', minWidth: 80 }}>만료일</Typography>
              <Typography sx={{ fontSize: 14, color: '#999', textDecoration: 'line-through' }}>
                {cancelConfirmDialog.data.oldExpire}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#333' }}>→</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#2E7D32' }}>
                {cancelConfirmDialog.data.newExpire}
              </Typography>
              {cancelConfirmDialog.data.restoreDays > 0 && (
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#E65100', ml: 'auto' }}>
                  -{cancelConfirmDialog.data.restoreDays}일
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button fullWidth onClick={() => setCancelConfirmDialog(prev => ({ ...prev, open: false }))}
            sx={{ height: 46, borderRadius: '8px', border: '1px solid #E0E0E0', color: '#333', fontSize: 15, fontWeight: 500 }}>
            취소
          </Button>
          <Button fullWidth onClick={cancelConfirmDialog.onConfirm}
            sx={{ height: 46, borderRadius: '8px', bgcolor: '#E53935', color: '#fff', fontSize: 15, fontWeight: 600, '&:hover': { bgcolor: '#C62828' } }}>
            해제
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
}
